import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllDesserts,
  getSingleDessert,
  createDessert,
  updateDessert,
  deleteDessert,
} from "@/api/new-api/staff-management/menu-manage/dessert";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllDesserts = createAsyncThunk(
  "desserts/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllDesserts(restaurant_id);
    if (res.status === "success") return res.data.desserts;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleDessert = createAsyncThunk(
  "desserts/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleDessert(id);
    if (res.status === "success") return res.data.dessert;
    return rejectWithValue(res.message);
  }
);

export const createNewDessert = createAsyncThunk(
  "desserts/create",
  async (data, { rejectWithValue }) => {
    const res = await createDessert(data);
    if (res.status === "success") return res.data.dessert;
    return rejectWithValue(res.message);
  }
);

export const updateExistingDessert = createAsyncThunk(
  "desserts/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateDessert(id, data);
    if (res.status === "success") return res.data.dessert;
    return rejectWithValue(res.message);
  }
);

export const deleteDessertById = createAsyncThunk(
  "desserts/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteDessert(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateDessertToggle = createAsyncThunk("dessertToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State

const initialState = {
  desserts: [],
  singleDessert: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const dessertSlice = createSlice({
  name: "desserts",
  initialState,
  reducers: {
    clearDessertMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllDesserts.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllDesserts.fulfilled, (state, action) => {
        state.getLoading = false;
        state.desserts = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllDesserts.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleDessert.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleDessert.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleDessert = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleDessert.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewDessert.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewDessert.fulfilled, (state, action) => {
        state.createLoading = false;
        state.desserts.push(action.payload);
        state.success = "Dessert created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewDessert.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingDessert.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingDessert.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.desserts = state.desserts.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        state.success = "Dessert updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingDessert.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteDessertById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteDessertById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.desserts = state.desserts.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Dessert deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteDessertById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateDessertToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateDessertToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.desserts = state.desserts.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateDessertToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearDessertMessages } = dessertSlice.actions;
export default dessertSlice.reducer;
